import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, OverlayView } from '@react-google-maps/api';
import PageLayout from '../layout/PageLayout';
import Header from '../layout/Header';
import SpotMarker from './SpotMarker';
import SpotDetailSheet from './SpotDetailSheet';
import LoadingSpinner from '../common/LoadingSpinner';
import useMapRallies from '../../hooks/useMapRallies';
import useMapSpots from '../../hooks/useMapSpots';
import useVersionCheck from '../../hooks/useVersionCheck';
import { findSpotsWithinRadius } from '../../utils/geoUtils';
import { ACTIVE_AREAS, SPOT_CONFIG } from '../../constants/mapConfig';
import FootprintMarker from '../Footprint/FootprintMarker';
import FootprintDetailSheet from '../Footprint/FootprintDetailSheet';
import DraggablePostPin from '../Footprint/DraggablePostPin';
import PostFormModal from '../Footprint/PostFormModal';
import useFootprints from '../../hooks/useFootprints';
import { FOOTPRINT_ZOOM_THRESHOLD } from '../../constants/mapConfig';

// ── 定数 ──────────────────────────────────────────────
// ズーム閾値（3段階）
//   zoom < DETAIL_ZOOM_THRESHOLD                            → 'overview' （isGlobal スポットのみ縮小ピン表示）
//   DETAIL_ZOOM_THRESHOLD ≤ zoom < EXPANDED_ZOOM_THRESHOLD  → 'detail'   （isGlobal スポットのみ・全国版カラー優先）
//   zoom ≥ EXPANDED_ZOOM_THRESHOLD                          → 'expanded' （全スポット・周辺ラリーカラー優先）
const DETAIL_ZOOM_THRESHOLD   = 9;
const EXPANDED_ZOOM_THRESHOLD =13;

// 日本全体が収まる初期カメラ位置
const JAPAN_DEFAULT_CENTER = { lat: 36.5, lng: 137.5 };
const JAPAN_DEFAULT_ZOOM = 6;

// Google Maps コンテナスタイル（画面全体に展開）
const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

/**
 * 色付きの足あとSVGアイコン
 * @param {string} color - 16進数カラーコード（例: '#22c55e' | '#3b82f6'）
 */
const FootprintSVGIcon = ({ color = '#3b82f6' }) => (
  <svg viewBox="0 0 62.84 64.9" style={{ width: '100%', height: '100%' }}>
    <g>
      <path fill={color} d="M61.54,9.44c-.33-.05-.73-.01-1.16.12l-12.78,3.75c-1.72.51-2.06,1.95-.76,3.19l1.41,1.34c1.95.78,3.72,2.2,4.69,3.88l.77,1.32,2.76,2.62c.43.41.87.64,1.28.7.85.13,1.58-.45,1.87-1.62l3.12-12.93c.3-1.32-.21-2.22-1.21-2.37Z"/>
      <path fill={color} d="M49.25,23.89c-.67-1.15-2.23-2.15-3.61-2.36-.13-.02-.27-.03-.4-.04l-23.53-.74c-.33,0-.67.04-1,.13-.31.71-.77,1.4-1.36,2.01l-2.45,2.59-9.42,21.03c-.61,1.34-.22,3.25.86,4.24l13.31,12.32c.9.83,2.51,1.54,3.88,1.75.26.04.51.06.75.06h19.71c1.47,0,3.18-1.07,3.84-2.4l8.86-18.16c.65-1.31.57-3.44-.18-4.7l-9.25-15.74Z"/>
      <path fill={color} d="M24.15,15.66c.18.03.36.04.57.03l13.31-.34c1.79-.04,2.5-1.34,1.56-2.87l-6.94-11.35c-.41-.67-.92-1.04-1.44-1.12-.67-.1-1.35.3-1.84,1.19l-6.37,11.69c-.77,1.4-.24,2.55,1.15,2.76Z"/>
      <path fill={color} d="M15.32,16.8l-12.94-3.12c-.1-.02-.2-.04-.29-.05-1.57-.24-2.44.8-1.97,2.43l3.76,12.77c.27.92.8,1.44,1.43,1.54.55.09,1.17-.16,1.75-.77l9.17-9.64c1.24-1.3.82-2.72-.92-3.15Z"/>
    </g>
  </svg>
);

// Google Maps オプション
const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  zoomControlOptions: { position: 9 }, // RIGHT_CENTER
  gestureHandling: 'greedy',
  mapId: undefined,
  clickableIcons: false, // POI（施設アイコン）のタップ・吹き出し表示を無効化
};

/**
 * NationalMapPage - 全国MAPビューのページコンポーネント
 *
 * 責務:
 * - ズームレベルの監視・表示モード切り替え（overview / detail の2段階）
 *     overview: 全スポットを縮小ピンで表示・全国エリアバナー
 *     detail  : 全スポットを通常ピンで表示・エリアバナー
 * - スポットピンの表示対象:
 *     hasRallyPin: false のラリー（例: all） → そのラリーの全スポット
 *     hasRallyPin: true のラリー（例: ueno） → areaRepresentativeSpotId のスポットのみ
 * - ボトムシートの表示制御
 * - 初期表示時に位置情報から5km判定
 * - サンプルデータトグル（REACT_APP_SHOW_SAMPLE_TOGGLE=true 時のみ）
 */
const NationalMapPage = () => {
  // ── バージョンチェック（/map を開くたびに Firestore と比較） ──
  useVersionCheck();

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
  });

  // サンプル/本番データ切り替え
  const [useMock, setUseMock] = useState(
    process.env.REACT_APP_USE_MOCK_MAP === 'true'
  );

  const { loading: ralliesLoading } = useMapRallies({ useMock });
  const { spots, loading: spotsLoading } = useMapSpots({ useMock });

  // 表示モード: 'overview' | 'detail'
  const [mapMode, setMapMode] = useState('overview');

  // 選択中スポットID（ボトムシート）
  const [selectedSpotId, setSelectedSpotId] = useState(null);

  const [center, setCenter] = useState(JAPAN_DEFAULT_CENTER);
  const [zoom, setZoom] = useState(JAPAN_DEFAULT_ZOOM);
  const mapRef = useRef(null);
  const initialMapStateRef = useRef(null); // 初期表示位置を保存（geolocation 確定後）

  // ── 足あと ──────────────────────────────────────────────────────
  const { posts: footprints, reload: reloadFootprints, updateReactionCount } = useFootprints();
  const [selectedFootprint, setSelectedFootprint] = useState(null);
  const [postFormLocation, setPostFormLocation] = useState(null);
  const [postFormIsSuperData, setPostFormIsSuperData] = useState(null); // 投稿フォームのすっごーい！足あとフラグ
  const [preDropMapState, setPreDropMapState] = useState(null);

  // ── 初期表示: 位置情報 + 5km判定 ──────────────────
  useEffect(() => {
    if (!navigator.geolocation || spots.length === 0) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const nearby = findSpotsWithinRadius({ lat: latitude, lng: longitude }, spots, 5);
        if (nearby.length > 0) {
          const nearestSpot = nearby[0];
          setCenter({ lat: nearestSpot.lat, lng: nearestSpot.lng });
          setZoom(DETAIL_ZOOM_THRESHOLD);
          setMapMode('detail');
          initialMapStateRef.current = {
            center: { lat: nearestSpot.lat, lng: nearestSpot.lng },
            zoom: DETAIL_ZOOM_THRESHOLD
          };
        } else {
          initialMapStateRef.current = { center: JAPAN_DEFAULT_CENTER, zoom: JAPAN_DEFAULT_ZOOM };
        }
      },
      () => {
        initialMapStateRef.current = { center: JAPAN_DEFAULT_CENTER, zoom: JAPAN_DEFAULT_ZOOM };
      },
      { timeout: 5000, maximumAge: 60000 }
    );
  }, [spots]);

  // ── 投稿フォーム位置が決定時: マップをパンしてズーム ──
  useEffect(() => {
    if (!postFormLocation || !mapRef.current) return;
    // マップをドロップ位置に中央化してズーム
    mapRef.current.panTo(postFormLocation);
    mapRef.current.setZoom(16);
  }, [postFormLocation]);

  // ── ズームレベル監視 ──────────────────────────────
  const handleZoomChanged = useCallback(() => {
    if (!mapRef.current) return;
    const currentZoom = mapRef.current.getZoom();
    setZoom(currentZoom);
    if (currentZoom >= EXPANDED_ZOOM_THRESHOLD) {
      setMapMode('expanded');
    } else if (currentZoom >= DETAIL_ZOOM_THRESHOLD) {
      setMapMode('detail');
    } else {
      setMapMode('overview');
      setSelectedSpotId(null);
    }
  }, []);

  // ── スポットタップ ──────────────────────────────
  const handleSpotTap = useCallback((spotId) => {
    console.debug(`[NationalMapPage] 📍 スポットタップ: spotId=${spotId}, time=${new Date().toISOString()}`);
    setSelectedSpotId(spotId);
  }, []);

  // ── マップロード完了 ──────────────────────────────
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // ── 表示スポット（SPOT_CONFIG 登録済み・ACTIVE_AREAS 内のみ）──
  // SPOT_CONFIG に登録されており、かつそのエリアが ACTIVE_AREAS に含まれるスポットを全表示
  const visibleSpots = useMemo(
    () =>
      spots.filter((s) => {
        const spotCfg = SPOT_CONFIG[s.id];
        if (!spotCfg) return false;
        if (spotCfg.hideFromMap) return false; // UMAなどマップ非表示スポット
        return ACTIVE_AREAS.includes(spotCfg.area);
      }),
    [spots]
  );

  // detail モードでは isGlobal: true のスポットのみ描画（expanded / overview は全スポット）
  const displaySpots = useMemo(
    () =>
      mapMode === 'detail'
        ? visibleSpots.filter((s) => SPOT_CONFIG[s.id]?.isGlobal === true)
        : visibleSpots,
    [visibleSpots, mapMode]
  );

  // GoalBanner に渡すスポット（エリア達成判定用）

  // ── ローディング / エラー表示 ─────────────────────
  if (loadError) {
    return (
      <PageLayout>
        <div className="flex h-screen items-center justify-center">
          <p className="text-red-500">地図の読み込みに失敗しました</p>
        </div>
      </PageLayout>
    );
  }

  const isMapLoading = !isLoaded || ralliesLoading || spotsLoading;

  return (
    <PageLayout>
      <div className="relative" style={{ height: '100dvh' }}>
        {/* ① ヘッダー（マップ上に固定オーバーレイ） */}
        <div className="pointer-events-auto absolute inset-x-0 top-0 z-20 bg-white/90 shadow-sm backdrop-blur-sm">
          <Header />
        </div>

        {/* ローディング中オーバーレイ（z-45: ピン z-49 より下、ボトムシート z-50 より下） */}
        {isMapLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white" style={{ zIndex: 45 }}>
            <LoadingSpinner />
          </div>
        )}

        {/* ② サンプルデータトグル（開発用） */}
        {process.env.REACT_APP_SHOW_SAMPLE_TOGGLE === 'true' && (
          <div className="absolute right-3 top-[100px] z-30 pointer-events-auto">
            <button
              onClick={() => setUseMock((prev) => !prev)}
              className="rounded-full px-3 py-1 text-xs font-bold text-white shadow"
              style={{ background: useMock ? '#2563EB' : '#B91C1C' }}
            >
              {useMock ? '🔵 サンプルデータ' : '🔴 本番データ'}
            </button>
          </div>
        )}

        {/* ④ Google Maps（画面全体に展開） */}
        {!isMapLoading && (
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={zoom}
          options={mapOptions}
          onLoad={onMapLoad}
          onZoomChanged={handleZoomChanged}
        >
          {/* 全スポット常時表示（overview: 縮小ピン / detail: isGlobal のみ通常ピン / expanded: 全スポット通常ピン） */}
          {displaySpots.map((spot) => (
            <SpotMarker
              key={spot.id}
              spot={spot}
              mapMode={mapMode}
              onTap={handleSpotTap}
            />
          ))}
          {/* 足あとピン（FOOTPRINT_ZOOM_THRESHOLD以上のズームでのみ表示） */}
          {zoom >= FOOTPRINT_ZOOM_THRESHOLD && footprints.map((post) => (
            <FootprintMarker
              key={post.postId}
              post={post}
              onClick={() => setSelectedFootprint(post)}
            />
          ))}

          {/* 投稿フォーム位置プレビューピン */}
          {postFormLocation && (
            <OverlayView
              position={postFormLocation}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div style={{ transform: 'translate(-50%, -100%)', cursor: 'default', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    backgroundColor: postFormIsSuperData ? '#22c55e' : '#3b82f6',
                    borderRadius: '50% 50% 50% 0',
                    transform: 'rotate(-45deg)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                    border: '2px solid white',
                    padding: '6px',
                  }}
                >
                  <div style={{ transform: 'rotate(45deg)', width: '100%', height: '100%' }}>
                    <FootprintSVGIcon color="white" />
                  </div>
                </div>
                {/* 作成中ラベル */}
                <div style={{
                  backgroundColor: 'rgba(0,0,0,0.65)',
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 'bold',
                  borderRadius: 99,
                  padding: '2px 8px',
                  whiteSpace: 'nowrap',
                  marginTop: 2,
                }}>
                  作成中
                </div>
              </div>
            </OverlayView>
          )}
        </GoogleMap>
        )}

        {/* ドラッグ用固定ピン（投稿フォーム表示中は非表示） */}
        {!postFormLocation && (
          <DraggablePostPin
            mapRef={mapRef}
            onDrop={(latLng) => {
              if (mapRef.current) {
                setPreDropMapState({
                  center: mapRef.current.getCenter().toJSON(),
                  zoom: mapRef.current.getZoom(),});
              }
              setPostFormLocation(latLng);
            }}
            onHomeClick={() => {
              if (!initialMapStateRef.current || !mapRef.current) return;
              mapRef.current.panTo(initialMapStateRef.current.center);
              mapRef.current.setZoom(initialMapStateRef.current.zoom);
            }}
          />
        )}

        {/* 初期位置に戻るボタン（空ボタン削除済み） */}

        {/* ⑤ スポット詳細ボトムシート */}
        {selectedSpotId && (
          <SpotDetailSheet
            spotId={selectedSpotId}
            onClose={() => setSelectedSpotId(null)}
            useMock={useMock}
            allSpots={spots}
          />
        )}

        {/* 足あと詳細ボトムシート */}
        {selectedFootprint && (
          <FootprintDetailSheet
            post={selectedFootprint}
            onClose={() => setSelectedFootprint(null)}
            onReaction={(postId, newCount) => {
              updateReactionCount(postId, newCount);
              setSelectedFootprint((prev) => prev ? { ...prev, reactionCount: newCount } : prev);
            }}
          />
        )}

        {/* 投稿フォーム（2ステップ） */}
        {postFormLocation && (
          <PostFormModal
            location={postFormLocation}
            onClose={() => {
              // 投稿せずに閉じた → ドロップ前の位置に戻す
              if (preDropMapState && mapRef.current) {
                mapRef.current.panTo(preDropMapState.center);
                mapRef.current.setZoom(preDropMapState.zoom);
              }
              setPostFormLocation(null);
              setPostFormIsSuperData(null);
              setPreDropMapState(null);
            }}
            onSuccess={() => {
              // 投稿完了 → 位置は戻さない（訪れた場所を認識させる）
              setPostFormLocation(null);
              setPostFormIsSuperData(null);
              setPreDropMapState(null);
              reloadFootprints(); // 投稿完了後に一覧を再取得
            }}
            onIsSuperChange={(isSuper) => setPostFormIsSuperData(isSuper)}
          />
        )}
      </div>
    </PageLayout>
  );
};

export default NationalMapPage;
